import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import type { IntlService } from 'ember-intl'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { ReportingUiScopeSelect } from 'teacher-ui-client/components/reporting/ui-scope-select'
import { ReportingPeriodSelect } from 'teacher-ui-client/components/reporting/period-select'
import { RecentWritingsListEWIAndLongWrite } from 'teacher-ui-client/components/writing-legends/recent-writings-list-ewi-long-write'
import { RecentWritingsListLesson } from 'teacher-ui-client/components/writing-legends/recent-writings-list-lesson'
import { RecentWritingsListPlacement } from 'teacher-ui-client/components/writing-legends/recent-writings-list-placement'
import type WritingLegendsRecentWritingRoute from 'teacher-ui-client/reporting/ui-scope/writing-legends/recent-writing/route'
import { UnderlineTabButton } from 'teacher-ui-client/components/underline-tab/button'
import { subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import type WritingLegendsRecentWritingController from 'teacher-ui-client/reporting/ui-scope/writing-legends/recent-writing/controller'
import { RecentWritingTab } from 'teacher-ui-client/reporting/ui-scope/writing-legends/recent-writing/controller'
import { ReportingControls } from 'teacher-ui-client/components/reporting/controls'

interface Signature {
  Args: {
    model: ModelFor<WritingLegendsRecentWritingRoute>
    controller: WritingLegendsRecentWritingController
  }
}

export class WritingLegendsRecentWritingPage extends Component<Signature> {
  @service intl!: IntlService

  @service subscriptionType!: SubscriptionTypeService

  get themeKey() {
    return subscriptionTypeToThemeKey(this.subscriptionType.current)
  }

  get writingTabs() {
    const tabs = Object.values(RecentWritingTab).map((tab) => {
      return {
        id: tab,
        name: this.intl.t(`pages.recentWriting.tabs.${tab}`),
        selected: this.args.controller.tab === tab,
      }
    })

    return tabs
  }

  setTab = (tab: RecentWritingTab) => {
    this.args.controller.tab = tab
    this.args.controller.pageNumber = 1 // reset the page number on tab change
  }

  <template>
    <div class="flex w-full flex-col">
      <PageHeader>
        <:title>
          <h1>
            {{t "pages.home.panels.recentWriting.title"}}
          </h1>
        </:title>
      </PageHeader>

      <ReportingControls>
        <ReportingUiScopeSelect class="shrink basis-80" />
        <ReportingPeriodSelect />
      </ReportingControls>

      <div class="mb-5 flex gap-2">
        {{#each this.writingTabs as |writingTab|}}
          <UnderlineTabButton
            @active={{writingTab.selected}}
            class="grow"
            @themeKey={{this.themeKey}}
            {{on "click" (fn this.setTab writingTab.id)}}
          >
            {{writingTab.name}}
          </UnderlineTabButton>
        {{/each}}
      </div>

      {{#if (eq @controller.tab RecentWritingTab.EWI)}}
        <RecentWritingsListEWIAndLongWrite
          @period={{@model.period}}
          @uiScope={{@model.uiScope}}
          @pageNumber={{@controller.pageNumber}}
          @perPage={{@controller.perPage}}
        />
      {{else if (eq @controller.tab RecentWritingTab.LessonActivity)}}
        <RecentWritingsListLesson
          @period={{@model.period}}
          @uiScope={{@model.uiScope}}
          @pageNumber={{@controller.pageNumber}}
          @perPage={{@controller.perPage}}
        />
      {{else if (eq @controller.tab RecentWritingTab.PlacementTest)}}
        <RecentWritingsListPlacement
          @period={{@model.period}}
          @uiScope={{@model.uiScope}}
          @pageNumber={{@controller.pageNumber}}
          @perPage={{@controller.perPage}}
        />
      {{else if (eq @controller.tab RecentWritingTab.LongWrites)}}
        <RecentWritingsListEWIAndLongWrite
          @period={{@model.period}}
          @uiScope={{@model.uiScope}}
          @pageNumber={{@controller.pageNumber}}
          @perPage={{@controller.perPage}}
          @type="long-write"
        />
      {{/if}}
    </div>
  </template>
}

export default RouteTemplate(WritingLegendsRecentWritingPage)
